import React from 'react';
import ReactGA from 'react-ga4';

const APP_LINKS = {
    SmartAssessment: {
        name: 'SmartAssessment',
        appStore: 'https://apps.apple.com/in/app/smartassessments/id6766214147',
        playStore: 'https://play.google.com/store/apps/details?id=ai.visist.smartassessments'
    },
    SmartJournal: {
        name: 'SmartJournal',
        appStore: 'https://apps.apple.com/us/app/visist-smartjournal/id6769680323',
        playStore: 'https://play.google.com/store/apps/details?id=ai.visist.smartjournal'
    }
};
// Support alias for backwards compatibility
APP_LINKS.SmartAssessments = APP_LINKS.SmartAssessment;

const AppleIcon = ({ className = "w-6 h-6" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.85c.66-.8 1.11-1.92.99-3.04-.96.04-2.13.64-2.82 1.44-.61.71-1.15 1.86-1.01 2.97 1.08.08 2.18-.57 2.84-1.37z" />
    </svg>
);

const PlayStoreIcon = ({ className = "w-6 h-6" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.609 1.814L13.792 12 3.61 22.186a2.372 2.372 0 0 1-.61-1.616V3.43c0-.629.222-1.205.609-1.616zM15.206 13.414l2.766 2.766-12.87 7.43a2.378 2.378 0 0 1-1.173.315l11.277-10.511zM17.972 10.636l3.534 2.04c.789.456.789 1.196 0 1.652l-3.534 2.04-2.766-2.766 2.766-2.966zM15.206 10.586L3.929.075a2.378 2.378 0 0 1 1.173.315l12.87 7.43-2.766 2.766z" />
    </svg>
);

export default function AppDownloadBadges({
    appName = 'SmartAssessment',
    appStoreUrl,
    playStoreUrl,
    theme = 'dark', // 'dark' | 'light' | 'glass'
    size = 'md',    // 'sm' | 'md' | 'lg'
    layout = 'row',  // 'row' | 'column'
    showTitle = false,
    titleText = 'Download Mobile App',
    className = ''
}) {
    const defaultData = APP_LINKS[appName] || APP_LINKS.SmartAssessment;
    const finalAppStoreUrl = appStoreUrl || defaultData.appStore;
    const finalPlayStoreUrl = playStoreUrl || defaultData.playStore;

    const trackClick = (store) => {
        try {
            ReactGA.event({
                category: 'MobileAppDownload',
                action: `download_${store.toLowerCase()}`,
                label: appName
            });
        } catch {}
    };

    // Styling variants
    const sizeClasses = {
        sm: {
            btn: 'px-3 py-1.5 text-xs rounded-lg gap-2',
            icon: 'w-4 h-4',
            sub: 'text-[9px]',
            main: 'text-xs font-semibold'
        },
        md: {
            btn: 'px-4 py-2.5 text-xs rounded-xl gap-3',
            icon: 'w-6 h-6',
            sub: 'text-[10px] tracking-wider uppercase opacity-80',
            main: 'text-sm font-bold tracking-tight'
        },
        lg: {
            btn: 'px-5 py-3 text-sm rounded-2xl gap-3.5',
            icon: 'w-7 h-7',
            sub: 'text-[11px] tracking-wider uppercase opacity-80',
            main: 'text-base font-extrabold tracking-tight'
        }
    }[size];

    const themeClasses = {
        dark: 'bg-[#0a0a0a] hover:bg-black text-white border border-white/15 hover:border-emerald/60 hover:shadow-[0_0_20px_rgba(16,185,129,0.25)]',
        light: 'bg-gray-900 hover:bg-black text-white border border-gray-800 hover:border-emerald hover:shadow-lg',
        glass: 'bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/20 hover:border-white/40 hover:shadow-[0_8px_25px_rgba(0,0,0,0.3)]',
        footer: 'bg-black/20 hover:bg-black/40 text-white border border-white/25 hover:border-white/50 backdrop-blur-sm shadow-md'
    }[theme] || 'bg-black/20 hover:bg-black/40 text-white border border-white/25 hover:border-white/50 backdrop-blur-sm shadow-md';

    const flexLayout = layout === 'column' ? 'flex-col items-stretch' : 'flex-col sm:flex-row flex-wrap items-center justify-center';

    return (
        <div className={`inline-flex flex-col gap-2 ${className}`}>
            {showTitle && (
                <p className={`text-xs font-bold uppercase tracking-wider ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                    {titleText}
                </p>
            )}
            <div className={`flex ${flexLayout} gap-3`}>
                {/* App Store Link */}
                <a
                    href={finalAppStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick('AppStore')}
                    aria-label={`Download ${appName} on Apple App Store`}
                    className={`inline-flex items-center justify-center transition-all duration-300 transform hover:-translate-y-0.5 group cursor-pointer ${sizeClasses.btn} ${themeClasses}`}
                >
                    <AppleIcon className={`${sizeClasses.icon} text-white group-hover:scale-110 transition-transform duration-300`} />
                    <div className="flex flex-col text-left leading-none">
                        <span className={sizeClasses.sub}>Download on the</span>
                        <span className={sizeClasses.main}>App Store</span>
                    </div>
                </a>

                {/* Google Play Link */}
                <a
                    href={finalPlayStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick('PlayStore')}
                    aria-label={`Get ${appName} on Google Play Store`}
                    className={`inline-flex items-center justify-center transition-all duration-300 transform hover:-translate-y-0.5 group cursor-pointer ${sizeClasses.btn} ${themeClasses}`}
                >
                    <PlayStoreIcon className={`${sizeClasses.icon} text-white group-hover:scale-110 transition-transform duration-300`} />
                    <div className="flex flex-col text-left leading-none">
                        <span className={sizeClasses.sub}>GET IT ON</span>
                        <span className={sizeClasses.main}>Play Store</span>
                    </div>
                </a>
            </div>
        </div>
    );
}
